import React, { useState } from 'react';
import { Envelope, ShareFat, Copy, Check } from '@phosphor-icons/react';
import { useParams } from 'react-router-dom';

import { FaceEmailType, checkFaceEmail, sendFaceEmail } from '../../UtilsEmail';

import { FACE_EMAIL_STATUS } from '../../constants';

import useSampleProjectData from '../../hooks/useSampleProjectData';

import { useProjectStore } from '../../stores/project';

import { Person, MAClips, PeopleEmailState } from '../../types';

import { handleCopy } from '../../utils';

import { Button } from '../general';
import { Form, InputGroup, FloatingLabel, InputGroupText, FormError } from '.';

interface Props {
  components?: {
    emailInput?: boolean;
    sendBtn?: 'rec' | 'square';
    copyBtn?: 'rec' | 'square';
  };
  type?: FaceEmailType;
  person: Person;
  maClips?: MAClips;
  emailState: PeopleEmailState;
  setEmailState: React.Dispatch<React.SetStateAction<PeopleEmailState>>;
  afterEmailSend?: () => void;
  fieldText?: string;
  link?: string;
  className?: string;
}

const EmailSendGroup: React.FC<Props> = (props) => {
  const { eventId = '' } = useParams();

  const {
    components = { emailInput: true, sendBtn: 'square', copyBtn: 'square' },
    type,
    person,
    maClips,
    emailState,
    setEmailState,
    afterEmailSend,
    fieldText,
    link,
    className,
  } = props;

  const { currentProject } = useProjectStore();

  const [isLinkCopied, setIsLinkCopied] = useState(false);

  const elementId = person.face.elementId;
  const personData = emailState[elementId] || {};

  // sample project data
  const { isSample } = useSampleProjectData(eventId);

  if (!currentProject) return null;

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailState((prev) => ({
      ...prev,
      [elementId]: { ...prev[elementId], email: e.target.value, err: null },
    }));
  };

  const handleSend = async () => {
    if (!type) return;

    const { isSuccess } = await sendFaceEmail({
      type,
      project: currentProject,
      person,
      maClips,
      emailState,
      setEmailState,
      showToast: true,
    });

    if (isSuccess && afterEmailSend) afterEmailSend();
  };

  const renderSendEmailBtn = () => {
    if (!components.sendBtn) return;

    let popoverBody = null;

    if (type === 'individual-creation')
      popoverBody = isSample
        ? 'Sharing email is not available in the sample project'
        : !currentProject.emailsData?.creationTemplateId
          ? 'First, select the email template above'
          : null;

    if (!popoverBody && !personData.email) popoverBody = 'No email address';

    return (
      <Button
        onClick={handleSend}
        variant={components.sendBtn === 'rec' ? 'primary-thin' : 'square'}
        colour="black"
        className="h-12 text-sm font-semibold"
        withGap
        disabled={!!popoverBody}
        allowPointerEvents
        isSubmitBtn
        type="button" // to prevent form submission on Enter key
        isProcessing={personData.status === FACE_EMAIL_STATUS.SENDING}
        isDone={personData.status === FACE_EMAIL_STATUS.SENT}
        spinner={{ size: 'md' }}
        popover={{
          header: 'Unable to share',
          body: popoverBody || '',
          placement: 'top',
          hide: !popoverBody,
        }}
        tooltip={{
          text: <div className="whitespace-nowrap">Send Email</div>,
          hide: components.sendBtn === 'rec',
        }}
      >
        <ShareFat size="1.25rem" />

        {components.sendBtn === 'rec' && (
          <span className="hidden sm:inline-block">Send Email</span>
        )}
      </Button>
    );
  };

  const renderCopyLinkBtn = () => {
    if (!components.copyBtn) return;

    return (
      <Button
        onClick={() => handleCopy(link ?? '', setIsLinkCopied, elementId)}
        variant={components.copyBtn === 'rec' ? 'primary-thin' : 'square'}
        colour="black-outline"
        className="h-12 text-sm font-semibold"
        withGap
        tooltip={{
          text: <div className="whitespace-nowrap">Copy Link</div>,
          hide: components.copyBtn === 'rec',
        }}
      >
        {isLinkCopied ? <Check size="1.25rem" /> : <Copy size="1.25rem" />}

        {components.copyBtn === 'rec' && (
          <span className="hidden sm:inline-block">Copy Link</span>
        )}
      </Button>
    );
  };

  return (
    <Form
      onSubmit={(e) => {
        e.preventDefault();
        checkFaceEmail(elementId, emailState, setEmailState);
      }}
      disabled={personData.status === FACE_EMAIL_STATUS.SENDING || isSample}
      formClassName={className}
      className="flex-row gap-2"
    >
      {components.emailInput && (
        <div className="grow">
          <InputGroup>
            <InputGroupText className="h-12">
              <Envelope />
            </InputGroupText>

            <FloatingLabel
              type="email"
              name="email"
              value={personData.email}
              onChange={handleEmailChange}
              placeholder={`Enter email address to ${fieldText}...`}
              isInvalid={!!personData.err}
              required
              className="grow"
              customHeight="h-12"
            />
          </InputGroup>

          <FormError errMsg={personData.err} />
        </div>
      )}

      {renderSendEmailBtn()}
      {renderCopyLinkBtn()}
    </Form>
  );
};

export default EmailSendGroup;
